import * as React from 'react'
import { cn } from '../../lib/utils'

export interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Completion from 0–100. Values outside the range are clamped. */
  value: number
  /**
   * Show a rounded "NN%" label before ("left") or after ("right") the bar.
   * Omit for a bare bar.
   */
  label?: 'left' | 'right'
}

/**
 * ProgressBar — a continuous progress indicator wired to First Bite tokens
 * (built to the design-system "Progress Bar" spec):
 *
 *   track   gray-200 (#e5e7eb), 8px tall, 4px radius
 *   fill    basil-700 (#087443), rounded, anchored left, width = `value`%
 *   label   optional 14px medium gray-700 percentage, on either side
 *
 * The bar flexes to fill its container, so width is controlled by the parent
 * (e.g. `className="flex-1"` inside a flex row, or a fixed-width wrapper).
 */
export const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ value, label, className, ...props }, ref) => {
    const pct = Math.max(0, Math.min(100, value))
    const rounded = Math.round(pct)

    const bar = (
      <div className="relative h-2 min-w-px flex-1">
        <div className="absolute inset-0 rounded bg-fbui-gray-200" />
        <div
          className="absolute inset-y-0 left-0 rounded bg-fbui-basil-700"
          style={{ width: `${pct}%` }}
        />
      </div>
    )

    const text = label ? (
      <span className="shrink-0 whitespace-nowrap text-sm font-medium tracking-[-0.2px] text-fbui-gray-700">
        {rounded}%
      </span>
    ) : null

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuenow={rounded}
        aria-valuemin={0}
        aria-valuemax={100}
        className={cn('flex items-center', label && 'gap-3', className)}
        {...props}
      >
        {label === 'left' && text}
        {bar}
        {label === 'right' && text}
      </div>
    )
  }
)
ProgressBar.displayName = 'ProgressBar'
